/**
 * Style diagnostics helper.
 *
 * Enable by adding `?debugStyles=1` to the URL (works in both Theme Editor preview and normal storefront).
 * Logs high-signal info about:
 * - compiled stylesheet presence (`compiled_assets/styles.css`)
 * - load/error events for that stylesheet link
 * - header/product-info computed styles and critical data-* state
 */
(function styleDiagnostics() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('debugStyles') !== '1') return;

    const now = () => Math.round(performance.now());
    const log = (...args) => console.log(`[style-diag ${now()}ms]`, ...args);

    const COMPILED_SELECTOR = 'link[rel="stylesheet"][href*="/compiled_assets/styles.css"]';
    const HEADER_SELECTOR = 'header-component#header-component';
    const PRODUCT_INFO_SELECTOR = '[data-testid="product-information"]';
    const HEADER_CTA_SELECTOR = '.header-cta-button';
    const VARIANT_PICKER_SELECTOR = 'variant-picker, .variant-picker';
    const BUY_BUTTONS_SELECTOR = '.buy-buttons-block, .product-form-buttons';
    const BUY_BUTTONS_LINKS = [
      'link[rel="stylesheet"][href*="buy-buttons-fallback.css"]',
      'link[rel="stylesheet"][href*="buy-buttons-overrides.css"]',
    ];

    function describeLink(link) {
      if (!(link instanceof HTMLLinkElement)) return null;
      return {
        hrefAttr: link.getAttribute('href'),
        hrefResolved: link.href,
        rel: link.rel,
        media: link.media,
        disabled: link.disabled,
        hasSheet: !!link.sheet,
      };
    }

    function findCompiledLink() {
      return document.head.querySelector(COMPILED_SELECTOR);
    }

    function findBuyButtonsLinks() {
      return BUY_BUTTONS_LINKS.map((sel) => document.head.querySelector(sel)).map((l) =>
        l instanceof HTMLLinkElement ? describeLink(l) : null
      );
    }

    function listStylesheets() {
      const sheets = Array.from(document.styleSheets || []);
      const mapped = sheets.map((s) => {
        /** @type {any} */
        const sheet = s;
        let rulesCount = null;
        let rulesError = null;
        try {
          rulesCount = sheet.cssRules?.length ?? null;
        } catch (e) {
          rulesError = String(e && e.name ? e.name : e);
        }
        return {
          href: sheet.href || null,
          disabled: !!sheet.disabled,
          rulesCount,
          rulesError,
        };
      });
      return mapped;
    }

    function getComputedSummary(selector) {
      const el = document.querySelector(selector);
      if (!(el instanceof HTMLElement)) return { selector, found: false };
      const cs = window.getComputedStyle(el);
      const rect = el.getBoundingClientRect();
      return {
        selector,
        found: true,
        tag: el.tagName.toLowerCase(),
        id: el.id || null,
        class: el.className || null,
        attrs: {
          'data-sticky-state': el.getAttribute('data-sticky-state'),
          'data-scroll-direction': el.getAttribute('data-scroll-direction'),
          'data-animating': el.hasAttribute('data-animating'),
          sticky: el.getAttribute('sticky'),
          transparent: el.getAttribute('transparent'),
        },
        computed: {
          display: cs.display,
          opacity: cs.opacity,
          visibility: cs.visibility,
          position: cs.position,
          transform: cs.transform,
          zIndex: cs.zIndex,
          top: cs.top,
        },
        rect: {
          top: Math.round(rect.top),
          left: Math.round(rect.left),
          width: Math.round(rect.width),
          height: Math.round(rect.height),
        },
      };
    }

    function getBodyVars() {
      const cs = window.getComputedStyle(document.body);
      const get = (name) => cs.getPropertyValue(name).trim() || null;
      return {
        '--header-height': get('--header-height'),
        '--header-group-height': get('--header-group-height'),
        '--transparent-header-offset-boolean': get('--transparent-header-offset-boolean'),
      };
    }

    function getHeaderStructure() {
      const header = document.querySelector(HEADER_SELECTOR);
      if (!(header instanceof HTMLElement)) return { found: false };

      const drawer = header.querySelector('[ref="headerDrawerContainer"]');
      const menu = header.querySelector('[ref="headerMenu"]');
      const headerRowTop = header.querySelector('[ref="headerRowTop"]');
      const columns = header.querySelector('.header__columns');

      return {
        found: true,
        hasHeaderDrawerContainerRef: !!drawer,
        hasHeaderMenuRef: !!menu,
        hasHeaderRowTopRef: !!headerRowTop,
        hasHeaderColumns: !!columns,
        headerColumns: columns instanceof HTMLElement ? getComputedSummary('.header__columns') : null,
      };
    }

    function getProductInfoStructure() {
      const root = document.querySelector(PRODUCT_INFO_SELECTOR);
      if (!(root instanceof HTMLElement)) return { found: false };
      const grid = root.querySelector('.product-information__grid');
      return {
        found: true,
        hasGrid: !!grid,
        grid: grid instanceof HTMLElement ? (() => {
          const cs = window.getComputedStyle(grid);
          return {
            display: cs.display,
            gridTemplateColumns: cs.gridTemplateColumns,
            gap: cs.gap,
            width: Math.round(grid.getBoundingClientRect().width),
          };
        })() : null,
      };
    }

    function getCtaSummary() {
      const el = document.querySelector(HEADER_CTA_SELECTOR);
      if (!(el instanceof HTMLElement)) return { found: false };
      const cs = window.getComputedStyle(el);
      return {
        found: true,
        display: cs.display,
        padding: `${cs.paddingTop} ${cs.paddingRight} ${cs.paddingBottom} ${cs.paddingLeft}`,
        backgroundColor: cs.backgroundColor,
        color: cs.color,
        borderRadius: cs.borderRadius,
        border: cs.border,
        font: cs.font,
        rect: (() => {
          const r = el.getBoundingClientRect();
          return { width: Math.round(r.width), height: Math.round(r.height) };
        })(),
      };
    }

    function getVariantPickerSummary() {
      const el = document.querySelector(VARIANT_PICKER_SELECTOR);
      if (!(el instanceof HTMLElement)) return { found: false };
      const cs = window.getComputedStyle(el);
      const pkg = el.classList.contains('variant-picker--package');
      const buttons = el.querySelector('.variant-option--buttons');
      const buttonsCs = buttons instanceof HTMLElement ? window.getComputedStyle(buttons) : null;
      return {
        found: true,
        packageMode: pkg,
        display: cs.display,
        width: cs.width,
        buttons: buttonsCs
          ? {
              display: buttonsCs.display,
              gridTemplateColumns: buttonsCs.gridTemplateColumns,
              gap: buttonsCs.gap,
            }
          : null,
      };
    }

    // Initial dump
    log('enabled', { href: window.location.href, designMode: !!window.Shopify?.designMode });

    const compiledLink = findCompiledLink();
    log('compiled link (initial)', compiledLink ? describeLink(compiledLink) : null);
    log(
      'stylesheets (initial)',
      listStylesheets().filter((s) => (s.href || '').includes('/compiled_assets/styles.css'))
    );
    log('buy-buttons links (initial)', findBuyButtonsLinks());

    // Watch compiled link changes (log only when it actually changes)
    let lastCompiledKey = null;
    const headObserver = new MutationObserver(() => {
      const link = findCompiledLink();
      const desc = link ? describeLink(link) : null;
      const key = desc ? `${desc.hrefResolved}|${desc.media}|${desc.disabled}|${desc.hasSheet}` : 'missing';
      if (key === lastCompiledKey) return;
      lastCompiledKey = key;
      log('compiled link (changed)', desc);
    });
    headObserver.observe(document.head, { childList: true });

    // Attach load/error listeners (best-effort; link might be replaced)
    function attachLinkListeners(link) {
      if (!(link instanceof HTMLLinkElement)) return;
      if (link.dataset.styleDiagListenersAttached === 'true') return;
      link.dataset.styleDiagListenersAttached = 'true';
      link.addEventListener('load', () => log('compiled link load', describeLink(link)));
      link.addEventListener('error', () => log('compiled link error', describeLink(link)));
    }
    if (compiledLink) attachLinkListeners(compiledLink);

    // Periodic snapshots to catch intermittent state changes
    const SNAPSHOT_MS = 750;
    let snapshots = 0;
    const maxSnapshots = 20;
    const interval = window.setInterval(() => {
      snapshots += 1;
      const link = findCompiledLink();
      if (link) attachLinkListeners(/** @type {HTMLLinkElement} */ (link));
      const header = getComputedSummary(HEADER_SELECTOR);
      const productInfo = getComputedSummary(PRODUCT_INFO_SELECTOR);
      const buyButtons = getComputedSummary(BUY_BUTTONS_SELECTOR);

      // Compact summary that's easy to paste from console without expanding objects.
      log('snapshot(summary)', {
        compiled: link ? describeLink(/** @type {HTMLLinkElement} */ (link)) : null,
        buyButtonsLinks: findBuyButtonsLinks(),
        bodyVars: getBodyVars(),
        header: header.found
          ? {
              opacity: header.computed.opacity,
              display: header.computed.display,
              position: header.computed.position,
              transform: header.computed.transform,
              rect: header.rect,
              attrs: header.attrs,
            }
          : header,
        productInfo: productInfo.found
          ? {
              display: productInfo.computed.display,
              rect: productInfo.rect,
            }
          : productInfo,
        buyButtons: buyButtons.found
          ? {
              display: buyButtons.computed.display,
              rect: buyButtons.rect,
            }
          : buyButtons,
        structure: {
          header: getHeaderStructure(),
          productInfo: getProductInfoStructure(),
          headerCta: getCtaSummary(),
          variantPicker: getVariantPickerSummary(),
        },
      });
      if (snapshots >= maxSnapshots) {
        clearInterval(interval);
        log('done');
      }
    }, SNAPSHOT_MS);
  } catch (e) {
    console.warn('[style-diag] failed to initialize', e);
  }
})();

